// Les fenêtres qui interrompent le jeu, par ordre de priorité.
import { useEffect } from 'preact/hooks';
import { PAGES } from '../content/pages';
import { fmtEur, fmtEurDelta, fmtEurPrecise, fmtPct, fmtPoints } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, Notice, PageId, ReviewData } from '../engine/state';
import { update } from '../engine/store';
import { chooseEventOption, closeEvent, finishScam, formatEventParams, getEvent } from '../game/events';
import { Modal } from './components';
import { KIND_LABEL, STAGE_INTRO, formatDuration } from './labels';
import { ScamGame } from './ScamGame';

export function Modals({ state }: { state: GameState }) {
  if (state.offlineSummary) return <OfflineModal state={state} />;
  if (state.stageIntro) {
    const intro = STAGE_INTRO[state.stageIntro];
    return (
      <Modal title={t(intro.title)}>
        <p>{t(intro.text)}</p>
        <button class="button-primary" onClick={() => update((s) => (s.stageIntro = null))}>
          {t('common.continue')}
        </button>
      </Modal>
    );
  }
  if (state.pendingEvent) return <EventModal state={state} />;
  if (state.unreadPages.length > 0) return <PageModal page={state.unreadPages[0]} />;
  if (state.pendingReview) return <ReviewModal review={state.pendingReview} />;
  return null;
}

function OfflineModal({ state }: { state: GameState }) {
  const summary = state.offlineSummary;
  if (!summary) return null;
  return (
    <Modal title={t('offline.title')}>
      <p>{summary.months > 0 ? t('offline.months', { months: summary.months }) : t('offline.noMonths')}</p>
      {summary.months > 0 && (
        <>
          <p>
            {t('offline.networth', {
              before: fmtEur(summary.networthBefore),
              after: fmtEur(summary.networthAfter),
            })}
          </p>
          {summary.interest > 0 && <p>{t('offline.interest', { amount: fmtEurPrecise(summary.interest) })}</p>}
        </>
      )}
      {summary.reserveAddedDays > 1 && (
        <p>{t('offline.reserve', { duration: formatDuration(summary.reserveAddedDays) })}</p>
      )}
      {state.energyUnlocked && <p class="muted">{t('offline.energy')}</p>}
      <button class="button-primary" onClick={() => update((s) => (s.offlineSummary = null))}>
        {t('offline.resume')}
      </button>
    </Modal>
  );
}

function EventModal({ state }: { state: GameState }) {
  const pending = state.pendingEvent;
  if (!pending) return null;
  const def = getEvent(pending.id);
  const params = formatEventParams(pending.params);

  if (pending.minigame === 'scam') {
    return (
      <Modal title={t('scam.title')} eyebrow={t(KIND_LABEL[def.kind])}>
        <ScamGame onDone={(found, wrong) => update((s) => finishScam(s, found, wrong))} />
      </Modal>
    );
  }

  if (pending.outcome) {
    const outcome = pending.outcome;
    const moneyDelta = pending.moneyDelta ?? 0;
    const wellbeingDelta = pending.wellbeingDelta ?? 0;
    const energyDelta = pending.energyDelta ?? 0;
    const hasDeltas =
      Math.round(moneyDelta) !== 0 || Math.abs(wellbeingDelta) >= 0.05 || Math.abs(energyDelta) >= 0.5;
    return (
      <Modal title={t(def.title)} eyebrow={t(KIND_LABEL[def.kind])}>
        <p class="lesson">{t(outcome.key, outcome.params)}</p>
        {hasDeltas && (
          <div class="deltas">
            {Math.abs(energyDelta) >= 0.5 && (
              <span class={energyDelta < 0 ? 'tone-bad' : 'tone-good'}>
                {t('event.energyChange', { points: fmtPoints(energyDelta) })}
              </span>
            )}
            {Math.round(moneyDelta) !== 0 && (
              <span class={moneyDelta < 0 ? 'tone-bad' : 'tone-good'}>
                {t('event.moneyChange', { amount: fmtEurDelta(moneyDelta) })}
              </span>
            )}
            {Math.abs(wellbeingDelta) >= 0.05 && (
              <span class={wellbeingDelta < 0 ? 'tone-bad' : 'tone-good'}>
                {t('event.wellbeingChange', { points: fmtPoints(wellbeingDelta) })}
              </span>
            )}
          </div>
        )}
        <button class="button-primary" onClick={() => update(closeEvent)}>
          {t('common.continue')}
        </button>
      </Modal>
    );
  }

  return (
    <Modal title={t(def.title)} eyebrow={t(KIND_LABEL[def.kind])}>
      <p>{t(def.text, params)}</p>
      {(pending.shock ?? 0) < 0 && (
        <p class="warning">{t('event.shock', { points: fmtPoints(pending.shock ?? 0) })}</p>
      )}
      <div class="choices">
        {def.choices.filter((choice) => !choice.visible || choice.visible(state, pending.params)).map((choice) => {
          const available = !choice.available || choice.available(state, pending.params);
          const note = available ? (choice.note?.(state, pending.params) ?? null) : null;
          return (
            <button
              key={choice.id}
              disabled={!available}
              onClick={() => update((s) => chooseEventOption(s, choice.id))}
            >
              {t(choice.label, params)}
              {!available && choice.unavailable && (
                <small>
                  {t(
                    typeof choice.unavailable === 'function'
                      ? choice.unavailable(state, pending.params)
                      : choice.unavailable,
                  )}
                </small>
              )}
              {note && <small>{t(note.key, note.params)}</small>}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function PageModal({ page }: { page: PageId }) {
  return (
    <Modal title={t(PAGES[page].title)} eyebrow={t('notebook.newPage')}>
      <p class="handwritten">{t(PAGES[page].text)}</p>
      <button class="button-primary" onClick={() => update((s) => s.unreadPages.shift())}>
        {t('notebook.close')}
      </button>
    </Modal>
  );
}

function ReviewModal({ review }: { review: ReviewData }) {
  return (
    <Modal title={t('review.title', { age: review.age })}>
      <p>{t('review.networth', { now: fmtEur(review.networth), delta: fmtEurDelta(review.networthDelta) })}</p>
      <p>{t('review.passive', { amount: fmtEurPrecise(review.passive) })}</p>
      <p>
        {review.income !== undefined && review.saved !== undefined
          ? t('review.savingsRateDetail', {
              rate: fmtPct(review.savingsRate),
              saved: fmtEur(review.saved),
              income: fmtEur(review.income),
            })
          : t('review.savingsRate', { rate: fmtPct(review.savingsRate) })}
      </p>
      <p>
        {review.salary > review.oldSalary
          ? t('review.raise', {
              pct: fmtPct(review.salary / review.oldSalary - 1),
              old: fmtEur(review.oldSalary),
              salary: fmtEur(review.salary),
            })
          : t('review.noRaise', { salary: fmtEur(review.salary) })}
      </p>
      {review.levelsGained !== undefined && (
        <p class="muted">{t('review.raiseRule', { count: review.levelsGained })}</p>
      )}
      <p class="muted">
        {review.spendingBefore !== undefined && review.spendingAfter !== undefined
          ? t('review.inflationAmounts', {
              before: fmtEur(review.spendingBefore),
              after: fmtEur(review.spendingAfter),
            })
          : t('review.inflation')}
      </p>
      <button class="button-primary" onClick={() => update((s) => (s.pendingReview = null))}>
        {t('common.continue')}
      </button>
    </Modal>
  );
}

/** Messages éphémères (paliers, promotions, objectifs), qui disparaissent seuls. */
export function Notices({ notices }: { notices: Notice[] }) {
  return (
    <div class="notices" aria-live="polite">
      {notices.map((notice) => (
        <NoticeItem key={notice.id} notice={notice} />
      ))}
    </div>
  );
}

function NoticeItem({ notice }: { notice: Notice }) {
  useEffect(() => {
    const timer = setTimeout(
      () => update((s) => (s.notices = s.notices.filter((item) => item.id !== notice.id))),
      6000,
    );
    return () => clearTimeout(timer);
  }, [notice.id]);
  return <div class="notice">{t(notice.key, notice.params)}</div>;
}
